import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import '../../../styles/Checkout.scss';
import AddContactModal from '../../../Components/modal/T_addContact';
import ContactListModal from '../../../Components/modal/T_contactList';
import { tryFetch } from '../../../Helpers/FetchAPI';
import { getTokenOrDefault } from '../../../Helpers/Storage';
import PageLoading from '../../../Pages/layout/PageLoading';
import FoundationModal from '../../../Components/modal/T_foundation';
import { calculateMerchandiseData, calculateLalamoveShipping, CartItemPayload, calculateMerchantVoucherData, calculatePlatformVoucherData} from '../../../Helpers/CalculateAPI';
import AddVoucherButton from './voucher/VoucherButton';
import VoucherModal from './voucher/VoucherModal';
import PlatformVoucherButton from './voucher/PlatformVoucherBtn';
import PlatformVoucherModal from './voucher/PlatformVoucherModal';
import { useSnackbar } from 'notistack';

interface AddressInfo {
  latitude: string;
  longitude: string;
  street_address_1: string;
  street_address_2: string;
  state_province: string;
  town_city: string;
  zip_code: string;
  country: string;
  landmark?: string;
  region: string;
}

interface ContactInfo {
  contact_id: string;
  label: string;
  contact_name: string;
  contact_phone: string;
  contact_email_address: string;
  is_default_address: boolean;
  delivery_instructions?: string
  address_info: AddressInfo;
}

interface CartItem {
  cart_item_id: string;
  product_name: string;
  variation_name: string | null;
  unit_price: string;
  quantity: number;
  total_amount: string;
  store_id: string;
  product_note: string;
}

interface Voucher {
  id: string;
  code: string;
  discount_type: string;
  discount_value: string;
}

interface ShippingStore {
  store_id: string;
  store_name: string;
  items: CartItem[];
  voucher: Voucher[] | null;
  shipping_fee: string;
  admin_fee: string;
  order_total: string;
}

interface OrderProcessingResponse {
  payment_url: string;
}

interface ContactListModalProps {
  contacts: ContactInfo[];
  onSave: (selectedContact: ContactInfo) => void;
  onClose: () => void;
}

const Checkout: React.FC = () => {
  const location = useLocation();
  const locationState = location.state as {
  selectedItems: CartItem[];
  totalAmount: string;
  } | null;

const selectedItems: CartItem[] = locationState?.selectedItems ?? [];
const totalAmount: string = locationState?.totalAmount ?? "0.00";
  // const { selectedItems = [], totalAmount = '0.00' } = location.state || {};
  const [contacts, setContacts] = useState<ContactInfo[]>([]);
  const [selectedContact, setSelectedContact] = useState<ContactInfo | null>(null);
  const [isContactListModalOpen, setIsContactListModalOpen] = useState(false);
  const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [loadingShipping, setLoadingShipping] = useState<boolean>(false);
  const [serviceFee, setServiceFee] = useState<number>(0);
  const [isPolicyChecked, setIsPolicyChecked] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isPolicyConfirmed, setIsPolicyConfirmed] = useState<boolean>(false);
  const [shippingError, setShippingError] = useState<string | null>(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState<boolean>(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [merchantNote, setMerchantNote] = useState<string>('');
  const [isFoundationModalOpen, setIsFoundationModalOpen] = useState(false);
  const [selectedFoundation, setSelectedFoundation] = useState<string | null>(null);
  const [shippingCart, setShippingCart] = useState<ShippingStore[]>([]);

  const [merchandiseSubtotal, setMerchandiseSubtotal] = useState<number>(0);
  const [baseTotal, setBaseTotal] = useState<number>(0);
  const [shippingFee, setShippingFee] = useState<number>(0);
  const [orderTotal, setOrderTotal] = useState<number>(0);
  const [requestId, setRequestId] = useState<string | null>(null);

  const [selectedPlatformVoucher, setSelectedPlatformVoucher] = useState<string | null>(null);
  const [selectedVoucher, setSelectedVoucher] = useState<{ store_id: string; voucher_id: string } | null>(null);
  const [appliedVouchers, setAppliedVouchers] = useState<Record<string, string>>({});
  const [isVoucherModalOpen, setIsVoucherModalOpen] = useState(false);
  const [activeVoucherStore, setActiveVoucherStore] = useState<ShippingStore | null>(null);
  const [voucherApplied, setVoucherApplied] = useState(false);
  const [isPlatformVoucherModalOpen, setIsPlatformVoucherModalOpen] = useState(false);
  const [platformVoucherDiscount, setPlatformVoucherDiscount] = useState<number>(0);
  const [appliedVoucherDiscounts, setAppliedVoucherDiscounts] = useState<Record<string, string>>({});

  const subtotal = merchandiseSubtotal > 0 ? merchandiseSubtotal : parseFloat(totalAmount);
  const currency = process.env.REACT_APP_CURRENCY;
  const displayTotal = subtotal + shippingFee;


  useEffect(() => {
    const token = getTokenOrDefault();
    const fetchContacts = async () => {
      try {
        const response = await tryFetch({
          url: '/g/address',
          method: 'GET',
          token,
        });
    
        if (response.is_success && Array.isArray(response.data)) {
          const contactsData = response.data.map((contact) => ({
            ...contact,
            is_default_address: Boolean(contact.is_default_address),
          })) as ContactInfo[];

          setContacts(contactsData);

          const defaultContact = contactsData.find((contact) => contact.is_default_address);
          setSelectedContact(defaultContact || contactsData[0] || null);
        } else {
          console.error('Error fetching contacts:', response.errors);
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
      }
    };

    fetchContacts();
  }, []);

  const truncateText = (text: string, limit: number) =>
    text.length > limit ? `${text.slice(0, limit)}...` : text;
    
  useEffect(() => {
  }, [shippingError]);useEffect(() => {
  if (selectedContact && selectedItems.length > 0) {
    calculateMerchandise();
  }
}, [selectedContact]);

  const calculateLalamoveAfterMerchandise = async (
  request_id: string,
  cartPayload: CartItemPayload[]
) => {
  try {
    const lalamoveData = await calculateLalamoveShipping(
      request_id,
      cartPayload
    );

    const computedShipping = lalamoveData.reduce(
      (total, store) =>
        total + parseFloat(store.shipping_fee || "0"),
      0
    );

    setShippingFee(computedShipping);

  } catch (error: any) {
    setShippingError(error.message);
  }
};


const calculateMerchandise = async () => {
  if (!selectedContact) return;

  try {
    setLoadingShipping(true);
    setShippingError(null);

    const uniqueStoreIds = Array.from(
      new Set(selectedItems.map((item) => item.store_id))
    ) as string[];

    const cartPayload: CartItemPayload[] = uniqueStoreIds.map((storeId) => ({
      store_id: storeId,
      items: selectedItems
        .filter((item) => item.store_id === storeId)
        .map((item) => item.cart_item_id),
      ...(appliedVouchers[storeId]
        ? { voucher_id: appliedVouchers[storeId] }
        : {}),
    }));

    const data = await calculateMerchandiseData(
      selectedContact.contact_id,
      cartPayload
    );

    setMerchandiseSubtotal(parseFloat(data.merchandise_subtotal || "0"));
    setBaseTotal(parseFloat(data.total_payable || "0"));
    setRequestId(data.request_id);
    setShippingCart(data.cart);

    await calculateLalamoveAfterMerchandise(
      data.request_id,
      cartPayload
    );

  } catch (error: any) {
    setShippingError(error.message);
  } finally {
    setLoadingShipping(false);
  }
};

const handleApplyVoucher = async (voucher_id: string) => {
  if (!selectedContact || !activeVoucherStore) return;

  try {
    setLoadingShipping(true);

    const updatedAppliedVouchers = {
      ...appliedVouchers,
      [activeVoucherStore.store_id]: voucher_id,
    };

    const uniqueStoreIds = Array.from(
      new Set(selectedItems.map((item) => item.store_id))
    ) as string[];

    const cartPayload: CartItemPayload[] = uniqueStoreIds.map((storeId) => ({
      store_id: storeId,
      items: selectedItems
        .filter((item) => item.store_id === storeId)
        .map((item) => item.cart_item_id),
      ...(updatedAppliedVouchers[storeId]
        ? { voucher_id: updatedAppliedVouchers[storeId] }
        : {}),
    }));

    const updatedData = await calculateMerchantVoucherData(
      selectedContact.contact_id,
      cartPayload,
      selectedPlatformVoucher || undefined
    );

    setAppliedVouchers(updatedAppliedVouchers);
    const updatedDiscounts: Record<string, string> = {
      ...appliedVoucherDiscounts,
    };

      updatedData.cart.forEach((store) => {
        if (updatedAppliedVouchers[store.store_id]) {
          const matchedVoucher = store.voucher?.find(
            (v) => v.id === updatedAppliedVouchers[store.store_id]
          );

          if (matchedVoucher) {
            updatedDiscounts[store.store_id] = matchedVoucher.discount_value;
          }
        }
      });

      setAppliedVoucherDiscounts(updatedDiscounts);


    setMerchandiseSubtotal(parseFloat(updatedData.merchandise_subtotal || "0"));
    setBaseTotal(parseFloat(updatedData.total_payable || "0"));
    setRequestId(updatedData.request_id);
    setShippingCart(updatedData.cart);

    await calculateLalamoveAfterMerchandise(
      updatedData.request_id,
      cartPayload
    );

    setIsVoucherModalOpen(false);

  } catch (err: any) {
    enqueueSnackbar(err.message, { variant: "error" });
  } finally {
    setLoadingShipping(false);
  }
};

const handleApplyPlatformVoucher = async (code: string) => {
  if (!selectedContact) return;

  try {
    setLoadingShipping(true);

    const uniqueStoreIds = Array.from(
      new Set(selectedItems.map((item) => item.store_id))
    ) as string[];

    const cartPayload: CartItemPayload[] = uniqueStoreIds.map((storeId) => ({
      store_id: storeId,
      items: selectedItems
        .filter((item) => item.store_id === storeId)
        .map((i) => i.cart_item_id),
      ...(appliedVouchers[storeId]
        ? { voucher_id: appliedVouchers[storeId] }
        : {}),
    }));

    const data = await calculatePlatformVoucherData(
      selectedContact.contact_id,
      cartPayload,
      code
    );

    const discount =
      merchandiseSubtotal - parseFloat(data.total_payable || "0");

    setPlatformVoucherDiscount(discount > 0 ? discount : 0);
    setSelectedPlatformVoucher(code);
    setMerchandiseSubtotal(parseFloat(data.total_payable || "0"));
    setBaseTotal(parseFloat(data.total_payable || "0"));
    setRequestId(data.request_id);
    setShippingCart(data.cart);

    await calculateLalamoveAfterMerchandise(
      data.request_id,
      cartPayload
    );

    enqueueSnackbar("Platform voucher applied successfully!", {
      variant: "success",
    });

    setIsPlatformVoucherModalOpen(false);

  } catch (err: any) {
    enqueueSnackbar(err.message, { variant: "error" });
  } finally {
    setLoadingShipping(false);
  }
};

  const handlePlaceOrderClick = () => {

  handlePlaceOrder();
};

const calculateAllTotals = async (
  merchandiseData: {
    request_id: string;
    merchandise_subtotal: string;
    total_payable: string;
    cart: ShippingStore[];
  },
  cartPayload: CartItemPayload[]
) => {
  try {
    setMerchandiseSubtotal(parseFloat(merchandiseData.merchandise_subtotal || "0"));
    setBaseTotal(parseFloat(merchandiseData.total_payable || "0"));
    setRequestId(merchandiseData.request_id);
    setShippingCart(merchandiseData.cart);

    const lalamoveData = await calculateLalamoveShipping(
      merchandiseData.request_id,
      cartPayload
    );

    const computedShipping = lalamoveData.reduce(
      (total, store) => total + parseFloat(store.shipping_fee || "0"),
      0
    );

    setShippingFee(computedShipping);

  } catch (error: any) {
    setShippingError(error.message);
  }
};

  const handlePlaceOrder = async () => {
    if (!selectedContact) {
      setAddressError("Please add address");
      return;
    } else {
      setAddressError(null); 
    }
    if (!isPolicyChecked || !isPolicyConfirmed) return;
    if (!requestId) {
      console.error('Request ID not available.');
      return;
    }

    setIsPlacingOrder(true);

    try {
      const token = getTokenOrDefault();
      const payload = {
        request_id: requestId,
        merchant_note: merchantNote || undefined,
        // voucher_id: appliedVouchers,
        voucher_id:
          Object.values(appliedVouchers).length > 0
            ? Object.values(appliedVouchers)[0]
            : undefined,
        platform_voucher_id: selectedPlatformVoucher || undefined,
      };

      const response = await tryFetch({
        url: '/g/process-order',
        method: 'POST',
        token,
        payload,
      });

      if (response.is_success && response.data) {
        const orderData = response.data as OrderProcessingResponse;
        window.location.href = orderData.payment_url;
      } else {
        console.error('Failed to process order:', response.errors);
        setIsPlacingOrder(false);
      }
    } catch (error) {
      console.error('Error processing order:', error);
      setIsPlacingOrder(false); 
    }
  };

  const handleAddContact = () => {
    setIsAddContactModalOpen(true);
  };

  const handleContactListModal = () => {
    setIsContactListModalOpen(true);
  };

  const handleContactSave = async (newContact: ContactInfo) => {
    try {
      const token = getTokenOrDefault();
      const response = await tryFetch({
        url: "/g/address",
        method: "GET",
        token,
      });
  
      if (response.is_success && Array.isArray(response.data)) {
        const updatedContacts = response.data.map((contact) => ({
          ...contact,
          is_default_address: Boolean(contact.is_default_address),
        })) as ContactInfo[];
  
        setContacts(updatedContacts);
  
        const addedContact = updatedContacts.find(
          (contact) => contact.contact_id === newContact.contact_id
        );
        setSelectedContact(addedContact || updatedContacts[0] || null);
      } else {
        console.error("Error fetching contacts after adding:", response.errors);
      }
    } catch (error) {
      console.error("Error fetching contacts after adding:", error);
    } finally {
      setIsAddContactModalOpen(false);
    }
  };

  const handlePolicyCheck = (type: 'policy-check' | 'policy-check-confirm') => {
    if (type === 'policy-check') {
      setIsPolicyChecked(prev => !prev);
    } else if (type === 'policy-check-confirm') {
      setIsPolicyConfirmed(prev => !prev);
    }
  };

  const hasShippingCart = Array.isArray(shippingCart) && shippingCart.length > 0;

  return (
    <div className="checkout-page">
      {loading && <PageLoading />}
      <h1 className="checkout-header">Checkout</h1>
      <hr className="checkout-divider" />

      <div className="checkout-container">
        <div className="left-section">
          <div className="contact-info">
            <h2 className="contact-text">Contact Information</h2>
            {addressError && <p className="co-error-message">{addressError}</p>}

            {selectedContact ? (
              <div className="contact-details">
                <p><strong>{selectedContact.contact_name}</strong></p>
                <p>+63 {selectedContact.contact_phone}</p>
                <p>{selectedContact.address_info.street_address_1}, {selectedContact.address_info.street_address_2}</p>
                <p>{selectedContact.address_info.town_city}, {selectedContact.address_info.state_province}, {selectedContact.address_info.region} {selectedContact.address_info.zip_code}</p>
                <button className="change-contact-button" onClick={handleContactListModal}>
                  Change Contact Info
                </button>
              </div>
            ) : (
              <button className="add-address-button" onClick={handleAddContact}>
                + Add Address
              </button>
            )}
          </div>

          <div className="note-to-merchant-container" style={{ position: 'relative' }}>
            <textarea
              className="note-input"
              placeholder="Note to Merchant (optional)"
              maxLength={50}
              value={merchantNote}
              onChange={(e) => setMerchantNote(e.target.value)}
              style={{ resize: 'none' , boxSizing: 'border-box'}}
            ></textarea>
            <div
              className="character-counter"
              style={{
                position: 'absolute',
                bottom: '8px',
                right: '10px',
                fontSize: '12px',
                color: merchantNote.length >= 40 ? 'red' : 'gray',
                pointerEvents: 'none',
              }}
            >
              {merchantNote.length}/50
            </div>
          </div>
        </div>

        <div className="right-section">
          <div className="order-summary">
            <h2>Order Summary</h2>
            <div className="order-items">
               {hasShippingCart ? (
                shippingCart.map((store) => (
                  <div key={store.store_id} className="store-block">

                    {store.items.map((item) => (
                      <div className="order-item" key={item.cart_item_id}>
                        <div className="item-row">
                          <div className="product-info">
                            {truncateText(item.product_name, 20)}
                            {item.variation_name && ` (${item.variation_name})`} x {item.quantity}
                          </div>
                          <div className="product-subtotal">
                            {currency} {parseFloat(item.total_amount).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))}

                    {store.voucher && store.voucher.length > 0 && (
                    <AddVoucherButton
                      onClick={() => {
                        setActiveVoucherStore(store);
                        setIsVoucherModalOpen(true);
                      }}
                      currency={currency}
                      appliedDiscount={appliedVoucherDiscounts[store.store_id] || null}
                      // appliedDiscount={
                      //   appliedVouchers[store.store_id]
                      //     ? store.voucher?.find(
                      //         (v) => v.id === appliedVouchers[store.store_id]
                      //       )?.discount_value || null
                      //     : null
                      // }
                    />
                  )}
                  </div>
                ))
              ) : (
                selectedItems.map((item: CartItem) => (
                  <div className="order-item" key={item.cart_item_id}>
                    <div className="item-row">
                      <div className="product-info">
                        {truncateText(item.product_name, 20)}
                        {item.variation_name && ` (${item.variation_name})`} x {item.quantity}
                      </div>
                      <div className="product-subtotal">
                        {currency} {parseFloat(item.total_amount).toFixed(2)}
                      </div>
                    </div>

                    {item.product_note && (
                      <div className="c-product-note">
                        <em>- {item.product_note}</em>
                      </div>
                    )}
                  </div>
                ))
              )}

            </div>
          </div>
          <div className="order-totals">
            <div className="order-total-row">
              <span>Subtotal:</span>
              <span className="total-amount">{currency} {subtotal.toFixed(2)}</span>
            </div>
            {/* <div className="order-total-row">
              <span>Platform Voucher:</span>
              <span className="total-amount"
              style={{
                  color: platformVoucherDiscount > 0 ? 'red' : undefined,
                }}>
                {platformVoucherDiscount > 0
                  ? `- ${currency} ${platformVoucherDiscount.toFixed(2)}`
                  : <PlatformVoucherButton onClick={() => setIsPlatformVoucherModalOpen(true)} />}
              </span>
            </div> */}
            <div className="order-total-row">
              <span>Shipping Fee:</span>
              <span className="total-amount">{currency} {shippingFee.toFixed(2)}</span>
            </div>
            <br></br>
            
            <div className="order-total-row grand-total">
              <span>Total:</span>
              <span className="total-amount">{currency} {displayTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
      {shippingError && <p className="shipping-error">{shippingError}</p>}

      <div className="checkout-footer">
        <div className="total-info">
          <p className="total-text">Total (No. of items: {Array.isArray(selectedItems) ? selectedItems.length : 0})</p>
          <p className="total-amount">{currency} {displayTotal.toFixed(2)}</p>
        </div>
        
        <div className="policy-checkbox">
          <input
            type="checkbox"
            id="policy-check"
            checked={isPolicyChecked}
            onChange={(e) => handlePolicyCheck('policy-check')}
          />
          <label htmlFor="policy-check">
            I have read and agreed to the&nbsp;
            <a href="/return-refund-policy" target="_blank" rel="noopener noreferrer">
              Return/Refund Policy
            </a>
          </label>
        </div>

        <div className="policy-checkbox">
          <input
            type="checkbox"
            id="policy-check-confirm"
            checked={isPolicyConfirmed}
            onChange={(e) => handlePolicyCheck('policy-check-confirm')}
          />
          <label htmlFor="policy-check-confirm">
          I have reviewed my order and I will proceed with my payment.
          </label>
        </div>
        <button
          className={`place-order-button ${(!isPolicyChecked || !isPolicyConfirmed || isPlacingOrder) ? 'disabled' : ''}`}
          disabled={!isPolicyChecked || !isPolicyConfirmed || isPlacingOrder}
          onClick={handlePlaceOrderClick}
        >
          {isPlacingOrder ? (
            <>
              <span className="checkout-spinner"></span> Placing Order...
            </>
          ) : (
            'Place Order'
          )}
        </button>
       
      </div>

      {isContactListModalOpen && (
        <ContactListModal
        contacts={contacts}
        onClose={() => setIsContactListModalOpen(false)}
        onSave={(selectedContact) => handleContactSave(selectedContact)}
      />
      )}


      {isAddContactModalOpen && (
        <AddContactModal
          onClose={() => setIsAddContactModalOpen(false)}
          onSave={handleContactSave} 
        />
      )}

      {isVoucherModalOpen && activeVoucherStore && (
        <VoucherModal
          storeName={activeVoucherStore.store_name}
          vouchers={activeVoucherStore.voucher ?? []}
          appliedVoucherId={appliedVouchers[activeVoucherStore.store_id] || null}
          onApply={handleApplyVoucher}
          onClose={() => setIsVoucherModalOpen(false)}
        />
      )}

      {/* {isPlatformVoucherModalOpen && (
      <PlatformVoucherModal
        onApply={handleApplyPlatformVoucher}
        onClose={() => setIsPlatformVoucherModalOpen(false)}
      />
    )} */}

       {isFoundationModalOpen && (
        <FoundationModal
          onClose={() => setIsFoundationModalOpen(false)}
          onConfirm={(foundation) => {
            setSelectedFoundation(foundation);
            setIsFoundationModalOpen(false);
            handlePlaceOrder();
          }}
        />
)}

    </div>
  );
};

export default Checkout;


//Payment Method
  {/* <div className="payment-method">
            <h2>Payment Method</h2>
            <label>
              <input type="radio" name="payment" /> Credit/Debit Card
            </label>
            <label>
              <input type="radio" name="payment" /> E-wallet
            </label>
            <label>
              <input type="radio" name="payment" disabled /> Pay On Delivery
            </label>
          </div> */}